import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { Observable, switchMap } from 'rxjs';

import { BaseDatatableStore, IBaseDatatableState } from '../../../../../state/base-datatable-store.service';
import { ELoadStatus } from '../../../../../state/state.interface';
import {
  HttpUtilitiesService,
  IBaseCrudResponse,
  IBaseResponse,
  IGenericCrudRequestConstructionParameters,
  IGetManyResponse,
} from '../../../../../utilities/http-utilities.service';
import {
  IEditWorkInstructionTemplate,
  IWorkInstructionTemplate,
  TAddWorkInstructionTemplate,
} from './work-instruction-templates.interface';

@Injectable()
export class WorkInstructionTemplatesStore extends BaseDatatableStore<
  IWorkInstructionTemplate,
  IBaseDatatableState<IWorkInstructionTemplate>
> {
  readonly createOne = this.effect((trigger$: Observable<TAddWorkInstructionTemplate>) =>
    trigger$.pipe(
      switchMap((item: TAddWorkInstructionTemplate) => {
        this.patchState({ singleCrudLoading: ELoadStatus.loading });

        return this.createWorkInstructionTemplate(item).pipe(
          tapResponse(
            (): void => {
              this.patchState({ singleCrudLoading: ELoadStatus.success });
            },
            // eslint-disable-next-line no-console,sonarjs/no-duplicate-string
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );
  readonly updateOne = this.effect((trigger$: Observable<IEditWorkInstructionTemplate>) =>
    trigger$.pipe(
      switchMap((item: IEditWorkInstructionTemplate) => {
        this.patchState({ singleCrudLoading: ELoadStatus.loading });

        return this.updateWorkInstructionTemplate(item).pipe(
          tapResponse(
            () => this.patchState({ singleCrudLoading: ELoadStatus.success }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  override readonly deleteOne = this.effect((trigger$: Observable<number>) =>
    trigger$.pipe(
      switchMap((id: number) => {
        this.patchState({ singleCrudLoading: ELoadStatus.loading });

        return this.deleteWorkInstructionTemplate(id).pipe(
          tapResponse(
            () => this.patchState({ singleCrudLoading: ELoadStatus.success }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  override readonly deleteBulk = this.effect((trigger$: Observable<number[]>) =>
    trigger$.pipe(
      switchMap((itemIds: number[]) => {
        this.patchState({ bulkCrudLoading: ELoadStatus.loading });

        return this.deleteWorkInstructionTemplates(itemIds).pipe(
          tapResponse(
            (response): void => {
              this.patchState({ bulkCrudLoading: ELoadStatus.success, bulkOperationFailedData: response.data });
            },
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  readonly loadWorkInstructionTemplates = this.effect(
    (trigger$: Observable<IGenericCrudRequestConstructionParameters>) =>
      trigger$.pipe(
        switchMap((params: IGenericCrudRequestConstructionParameters) => {
          const httpParams: HttpParams = this.httpUtilities.insertGenericCrudRequestParameters(params);
          this.patchState({ dataLoading: ELoadStatus.loading });

          return this.getWorkInstructionTemplates(httpParams).pipe(
            tapResponse(
              (response: IGetManyResponse<IWorkInstructionTemplate>) =>
                this.patchState({ data: response.data, dataLoading: ELoadStatus.success, dataTotal: response.total }),
              // eslint-disable-next-line no-console
              (error) => console.error('Error loading data', error),
            ),
          );
        }),
      ),
  );

  constructor(
    private readonly http: HttpClient,
    private readonly httpUtilities: HttpUtilitiesService,
  ) {
    super({
      bulkCrudLoading: ELoadStatus.initial,
      bulkOperationFailedData: [],
      data: [],
      dataLoading: ELoadStatus.initial,
      dataTotal: 0,
      singleCrudLoading: ELoadStatus.initial,
    });
  }

  private createWorkInstructionTemplate(
    body: TAddWorkInstructionTemplate,
  ): Observable<IBaseResponse<IWorkInstructionTemplate>> {
    return this.http.post<IBaseResponse<IWorkInstructionTemplate>>('guide-templates', body);
  }

  private updateWorkInstructionTemplate(
    editWorkInstructionTemplate: IEditWorkInstructionTemplate,
  ): Observable<IBaseResponse<IWorkInstructionTemplate>> {
    return this.http.patch<IBaseResponse<IWorkInstructionTemplate>>(
      `guide-templates/${editWorkInstructionTemplate.id}`,
      editWorkInstructionTemplate.dto,
    );
  }

  private deleteWorkInstructionTemplate(id: number): Observable<IBaseResponse<IWorkInstructionTemplate>> {
    return this.http.delete<IBaseResponse<IWorkInstructionTemplate>>(`guide-templates/${id}`);
  }

  private getWorkInstructionTemplates(params: HttpParams): Observable<IGetManyResponse<IWorkInstructionTemplate>> {
    return this.http.get<IGetManyResponse<IWorkInstructionTemplate>>('guide-templates', { params });
  }

  private deleteWorkInstructionTemplates(ids: number[]): Observable<IBaseResponse<IBaseCrudResponse[]>> {
    return this.http.delete<IBaseResponse<IBaseCrudResponse[]>>('guide-templates/bulk/delete', {
      body: { payload: ids },
    });
  }
}
